import { CommonModule } from "@angular/common";
import {
  Component,
  DestroyRef,
  inject,
  OnDestroy,
  OnInit,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormBuilder, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { Router, RouterModule } from "@angular/router";
import { NxErrorModule } from "@aposin/ng-aquila/base";
import { NxButtonModule } from "@aposin/ng-aquila/button";
import { NxFormfieldModule } from "@aposin/ng-aquila/formfield";
import { NxGridModule } from "@aposin/ng-aquila/grid";
import { NxIconModule } from "@aposin/ng-aquila/icon";
import { NxInputModule } from "@aposin/ng-aquila/input";
import { NxLinkModule } from "@aposin/ng-aquila/link";
import { TranslateModule } from "@ngx-translate/core";
import { Select, Store } from "@ngxs/store";
import { CrossAppNavigationService } from "@services/cross-app-navigation/cross-app-navigation.service";
import { NxPasswordTouchToggleDirective } from "@shared/directives/nx-password-touch-toggle.directive";
import { debounceTime } from "rxjs/operators";
import { Scroller } from "src/app/shared/classes/scroller";
import {
  GET_SECRET_QUESTION,
  RESET_LOGIN_RESPONSE,
  SAVE_USER_VALIDATION,
  SUBMIT_VALIDATION_QUESTION,
} from "src/app/store/user/user.action";
import { UserState } from "src/app/store/user/user.state";

import { AuthSideHeaderComponent } from "../auth-side-header/auth-side-header.component";
import { PendingLoginValidationService } from "../pending-login-validation.service";

@Component({
  selector: "app-secret-answer",
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    NxButtonModule,
    NxErrorModule,
    NxFormfieldModule,
    NxGridModule,
    NxIconModule,
    NxInputModule,
    NxLinkModule,
    TranslateModule,
    AuthSideHeaderComponent,
    NxPasswordTouchToggleDirective,
  ],
  templateUrl: "./secret-answer.component.html",
  styleUrl: "./secret-answer.component.scss",
})
export class SecretAnswerComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  protected secretAnswerForm: FormGroup;
  @Select(UserState.secretQuestionDescp) protected secretQuestionDescp$;
  @Select(UserState.getSecretQuestionError) protected secretQuestionError$;

  private formDirty: boolean = false;
  private success: boolean = false;

  constructor(
    private readonly fb: FormBuilder,
    private readonly store: Store,
    private readonly router: Router,
    private readonly crossAppNavigationService: CrossAppNavigationService,
    private readonly pendingLoginValidationService: PendingLoginValidationService,
  ) {}

  ngOnInit() {
    this.secretAnswerForm = this.fb.group({
      secretAnswer: [""],
    });
    this.restorePendingUserValidation();
    this.getSecretQuestion();
    this.checkSecretAnswer();
    Scroller.scrollToTop();
  }

  private restorePendingUserValidation() {
    const user = this.store.selectSnapshot<any>((state) => state.UserState);
    if (user.tempUserId && user.tempPass) {
      return;
    }

    const pendingValidation = this.pendingLoginValidationService.peek();
    if (!pendingValidation) {
      return;
    }

    this.store.dispatch(new SAVE_USER_VALIDATION(pendingValidation));
  }

  private getSecretQuestion() {
    const user = this.store.selectSnapshot<any>((state) => state.UserState);
    if (!user.tempUserId) {
      return;
    }
    this.store.dispatch(new GET_SECRET_QUESTION(user.tempUserId));
  }

  private checkSecretAnswer() {
    this.secretAnswerForm
      .get("secretAnswer")
      .valueChanges.pipe(debounceTime(250), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.formDirty = this.secretAnswerForm.dirty;
      });
  }

  protected onSubmit() {
    const user = this.store.selectSnapshot<any>((state) => state.UserState);
    const validatelogin = {
      userId: user.tempUserId,
      password: user.tempPass,
      secretAnswer: this.secretAnswerForm.get("secretAnswer").value,
      secretQuestion: user.secretQuestionNo,
      viaSecretAnswer: true,
    };

    this.store
      .dispatch(new SUBMIT_VALIDATION_QUESTION(validatelogin))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        const userState = this.store.selectSnapshot<any>(
          (state) => state.UserState,
        );
        if (userState.secretAnswerwrongpass) {
          return;
        } else if (!userState.secretQuesionError) {
          this.markSuccessfulExit();
          this.pendingLoginValidationService.clear();
          // Correct answer — backend will determine redirect based on user state:
          // isUpdatePassword → /auth/first-time-login
          // isPasswordExpired → /auth/password-expired
          // normal → Alpha dashboard (/)
          // TODO: Confirm with backend that redirectToAlphaSatPath(userId) returns state-aware
          // redirectUrls for all three cases above.
          localStorage.setItem("loginSource", "sat");

          if (userState.isUpdatePassword) {
            this.router.navigate(["/first-time-login"]);
          } else if (userState.isPasswordExpired) {
            this.router.navigate(["/password-expired"]);
          } else {
            this.crossAppNavigationService.redirectToAlphaSatPath(
              "/home?token=TOKEN",
            );
          }
        }
      });
  }

  private markSuccessfulExit() {
    this.success = true;
    this.formDirty = false;
    this.secretAnswerForm.markAsPristine();
    this.secretAnswerForm.markAsUntouched();
  }

  ngOnDestroy() {
    this.pendingLoginValidationService.clear();
    this.store.dispatch(new RESET_LOGIN_RESPONSE());
  }
}
